/* =========================================================================
   Character UI — overlays + home greeting card + art probing.
   - charactersOverlayHTML: the non-blocking corner #charPop bubble, the blocking
     #rankUpOverlay ("A new rank"), and the GTKY moment overlay.
   - initCharacterArt(): Image()/video probe against the media manifest slots so
     finished art can land ANY time with zero code edits (emoji fallback until).
   - triggerCharPop(): 2.2s corner pop (copies triggerAngel's lifecycle).
   - triggerRankUp(): blocking overlay; the brightness-tier CSS transition IS the
     grow animation; choir + tap/9s dismiss (copies triggerDidYouKnow lifecycle).
   - renderCompanionHome(): the home greeting card (art badge + time-of-day line +
     feast sub-line + Daily Blessing candle chip + streak chip).
   ========================================================================= */
import { esc } from '../engine';
import { icon } from '../ui/icons';
import { track } from '../telemetry';
import { sndChoir } from '../audio';
import { isAuthenticated, getProfileMe } from '../api';
import { getLine } from './registry';
import type { Moment, Pose } from './types';
import { type Rank, tierForRank, rankForXp } from '../data/ranksData';
import { greetingPeriod, greetingShouldRefresh } from './logic';
import { localDateString } from '../dailyBlessing';
import { isLitToday, currentRemembranceName } from '../memorial';
import { loadToday } from '../today';
import { gtkyOverlayHTML } from '../gtky';
import { resolvePlayerName } from './name';
import { setAuthedHistory, authedWithHistoryFromStats, isTheoIntroduced } from '../progression';

/** Feast de-emphasis (Eric: "hide for now"). Flip back to true to restore the
    Theo greeting-card feast sub-line + its tap-into-the-moment shortcut. */
const SHOW_FEAST_ON_HOME = false;

const THEO_EMOJI = '🧑‍🦲';
const ART_PATHS: Record<Pose, string> = {
  greet: 'media/characters/theo-greet.png',
  welcome: 'media/characters/theo-welcome.png',
  teach: 'media/characters/theo-teach.png',
  pray: 'media/characters/theo-pray.png',
};
const IDLE_LOOP = 'media/theo-idle-loop.mp4';
/** Conversation-portrait slot (also used by the GTKY overlay via its own probe). */
const HEADSHOT_PATH = 'media/characters/theo-headshot.png';

const artAvail: Record<Pose, boolean> = { greet: false, welcome: false, teach: false, pray: false };
let idleLoopAvail = false;
let headshotAvail = false;
let introVideoAvail = false; // media/theo-intro.mp4 — plays inside "Meet Brother Theo"
let celebrateVideoAvail = false; // media/theo-celebrate.mp4 — plays inside rank-up / road-opens

const INTRO_VIDEO = 'media/theo-intro.mp4';
const CELEBRATE_VIDEO = 'media/theo-celebrate.mp4';

/** Probe an optional overlay video (same graceful pattern as the idle loop). */
function probeOverlayVideo(src: string, mark: () => void): void {
  try {
    const v = document.createElement('video');
    v.muted = true;
    v.preload = 'metadata';
    v.onloadeddata = mark;
    v.onerror = () => {};
    v.src = src;
  } catch {
    /* no <video> support — static art / emoji only */
  }
}

/** Reduced-motion users get the still art instead of an animation. */
function overlayVideoOk(avail: boolean): boolean {
  try {
    return avail && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return avail;
  }
}

/** Markup for an overlay's media area when its video is available: muted, autoplay,
    loop, inline — the overlay's tap/auto-dismiss stays in charge of lifetime. */
function overlayVideoInner(src: string): string {
  return `<video class="ru-video" src="${src}" muted autoplay loop playsinline></video>`;
}

/** Last known authed rank level (drives Theo's brightness); guests stay tier 1. */
let knownRankLevel = 0;

function el(id: string): HTMLElement | null {
  return document.getElementById(id);
}

export const charactersOverlayHTML = /* html */ `
<!-- CORRECT-RUN / HINT / DYK corner pop (non-blocking) -->
<div id="charPop" class="char-pop hidden" aria-hidden="true">
  <div class="char-pop-badge char-art tier-1" id="charPopBadge">${THEO_EMOJI}</div>
  <div class="char-pop-bubble" id="charPopText"></div>
</div>

<!-- RANK-UP celebration (blocking; brightness transition = the grow animation) -->
<div id="rankUpOverlay" class="rankup hidden" role="dialog" aria-modal="true" aria-hidden="true">
  <div class="ru-dim"></div>
  <div class="ru-stage">
    <div class="ru-badge char-art tier-1" id="ruBadge">${THEO_EMOJI}</div>
    <div class="ru-title" id="ruTitle">A new rank</div>
    <div class="ru-rank" id="ruRank"></div>
    <div class="ru-sub" id="ruSub"></div>
    <div class="ru-close">Tap anywhere to continue</div>
  </div>
</div>
${gtkyOverlayHTML}`;

/** Probe the media manifest slots; re-render the home card as each asset lands. */
export function initCharacterArt(onReady?: () => void): void {
  (Object.keys(ART_PATHS) as Pose[]).forEach((pose) => {
    const img = new Image();
    img.onload = () => {
      artAvail[pose] = true;
      if (onReady) onReady();
    };
    img.onerror = () => {
      /* asset not present yet — emoji fallback stays */
    };
    img.src = ART_PATHS[pose];
  });
  // Headshot portrait (introduction overlay) — same probe/fallback contract.
  const hs = new Image();
  hs.onload = () => {
    headshotAvail = true;
    if (onReady) onReady();
  };
  hs.onerror = () => {};
  hs.src = HEADSHOT_PATH;
  try {
    const v = document.createElement('video');
    v.muted = true;
    v.preload = 'metadata';
    v.onloadeddata = () => {
      idleLoopAvail = true;
      if (onReady) onReady();
    };
    v.onerror = () => {};
    v.src = IDLE_LOOP;
  } catch {
    /* no <video> support — static art / emoji only */
  }
  probeOverlayVideo(INTRO_VIDEO, () => {
    introVideoAvail = true;
  });
  probeOverlayVideo(CELEBRATE_VIDEO, () => {
    celebrateVideoAvail = true;
  });
}

/** Inner markup for an art badge: idle-loop video → still art → emoji fallback. */
function artInner(pose: Pose, allowVideo: boolean): string {
  if (allowVideo && idleLoopAvail) {
    return `<video class="char-art-media" src="${IDLE_LOOP}" muted loop playsinline autoplay></video>`;
  }
  if (artAvail[pose]) {
    return `<img class="char-art-media" src="${ART_PATHS[pose]}" alt="Brother Theo" />`;
  }
  return `<span class="char-art-emoji">${THEO_EMOJI}</span>`;
}

/* ---------- corner pop (correct runs / hint / dyk framing) ---------- */
let charPopTimer: ReturnType<typeof setTimeout> | null = null;

export function triggerCharPop(text: string): void {
  const pop = el('charPop');
  if (!pop) return;
  const badge = el('charPopBadge');
  const txt = el('charPopText');
  const tier = tierForRank(knownRankLevel);
  if (badge) {
    badge.className = `char-pop-badge char-art tier-${tier}`;
    badge.innerHTML = artInner('teach', false);
  }
  if (txt) txt.textContent = text;
  pop.classList.remove('hidden');
  pop.setAttribute('aria-hidden', 'false');
  void pop.offsetWidth;
  pop.classList.add('show');
  if (charPopTimer) clearTimeout(charPopTimer);
  // Dwell scales with the line — a flat 4s cut off Theo's longer encouragement
  // lines (Eric, 2026-09-06: "they don't last long and I cannot read them").
  // ~60ms/char reading pace on top of a 1.6s notice window, clamped 4-10s.
  const dwellMs = Math.min(10_000, Math.max(4000, 1600 + text.length * 60));
  charPopTimer = setTimeout(() => {
    pop.classList.remove('show');
    pop.classList.add('hidden');
    pop.setAttribute('aria-hidden', 'true');
  }, dwellMs);
  liftAboveButtons(pop);
}

/** Never park the bubble on top of a control (Eric 2026-09-06: it sat on the
    friendly "Next question →" / end-card buttons). Callers often unhide those
    buttons in the same tick they trigger the pop, so probe a macrotask later
    (setTimeout, not rAF — rAF never fires in a throttled/background tab):
    hit-test beneath the bubble (it is pointer-events:none, so elementsFromPoint
    sees through it) and lift it just above the highest button it covers. */
function liftAboveButtons(pop: HTMLElement): void {
  pop.style.bottom = ''; // back to the CSS default before measuring
  if (typeof document.elementsFromPoint !== 'function') return; // jsdom
  setTimeout(() => {
    if (!pop.classList.contains('show')) return;
    const host = pop.offsetParent as HTMLElement | null;
    if (!host) return;
    const r = pop.getBoundingClientRect();
    let btnTop = Infinity;
    for (const x of [r.left + 4, r.left + r.width / 2, r.right - 4]) {
      for (const y of [r.top + 4, r.top + r.height / 2, r.bottom - 4]) {
        for (const under of document.elementsFromPoint(x, y)) {
          const b = (under as HTMLElement).closest?.('button');
          if (b && !pop.contains(b)) btnTop = Math.min(btnTop, b.getBoundingClientRect().top);
        }
      }
    }
    if (btnTop === Infinity) return;
    const hostRect = host.getBoundingClientRect();
    const lifted = Math.round(hostRect.bottom - btnTop + 10);
    // Don't lift past the top of the host — a bubble mid-screen beats one clipped off.
    const max = Math.max(0, Math.round(hostRect.height - r.height - 20));
    pop.style.bottom = `${Math.min(lifted, max)}px`;
  }, 50);
}

/* ---------- rank-up overlay ("A new rank") ---------- */

/** Shared show + choir + tap/auto-dismiss lifecycle for the blocking
    #rankUpOverlay (rank-up celebration, deep-mode unlock, Theo introduction).
    `onDone` fires exactly once after dismissal. */
function presentRankOverlay(ov: HTMLElement, onDone?: () => void, autoMs = 9000): void {
  ov.classList.remove('hidden');
  ov.setAttribute('aria-hidden', 'false');
  void ov.offsetWidth;
  ov.classList.add('show');
  try {
    sndChoir();
  } catch {
    /* audio locked — silent celebration */
  }
  let finished = false;
  const finish = (): void => {
    if (finished) return;
    finished = true;
    clearTimeout(autoT);
    ov.classList.add('hidden');
    ov.classList.remove('show');
    ov.setAttribute('aria-hidden', 'true');
    ov.removeEventListener('pointerdown', finish);
    if (onDone) onDone();
  };
  const autoT = setTimeout(finish, autoMs);
  ov.addEventListener('pointerdown', finish);
}

export function triggerRankUp(rank: Rank): void {
  const ov = el('rankUpOverlay');
  if (!ov) return;
  knownRankLevel = rank.level;
  const tier = tierForRank(rank.level);
  const badge = el('ruBadge');
  if (badge) {
    badge.className = `ru-badge char-art tier-${tier}`;
    // Celebration video when it has landed; the pose art keeps the brightness story otherwise.
    badge.innerHTML = overlayVideoOk(celebrateVideoAvail) ? overlayVideoInner(CELEBRATE_VIDEO) : artInner('welcome', false);
  }
  const title = el('ruTitle');
  if (title) title.textContent = 'A new rank';
  const rk = el('ruRank');
  if (rk) rk.textContent = `${rank.icon} ${rank.name}`;
  const sub = el('ruSub');
  if (sub) sub.textContent = getLine('theo', 'rankup');

  presentRankOverlay(ov);
}

/** One-time "deep mode unlocked" moment — reuses the rank-up overlay chrome with
    a bespoke title/line the moment the lifetime counter crosses the threshold. */
export function triggerDeepModeUnlock(): void {
  const ov = el('rankUpOverlay');
  if (!ov) return;
  const tier = tierForRank(knownRankLevel);
  const badge = el('ruBadge');
  if (badge) {
    badge.className = `ru-badge char-art tier-${tier}`;
    badge.innerHTML = overlayVideoOk(celebrateVideoAvail) ? overlayVideoInner(CELEBRATE_VIDEO) : artInner('welcome', false);
  }
  const title = el('ruTitle');
  if (title) title.textContent = 'The road opens';
  const rk = el('ruRank');
  if (rk) rk.innerHTML = `${icon('compass', 16)} Deeper paths are open`;
  const name = resolvePlayerName();
  const sub = el('ruSub');
  if (sub) {
    sub.textContent =
      `You've been faithful in the small steps${name ? ', ' + name : ''}. Deeper paths are opening before us. ` +
      `Today's Pilgrimage, the map, and your Collections now await in the menu.`;
  }
  presentRankOverlay(ov);
}

/** One-time Theo INTRODUCTION (staged onboarding: characters earn their entrance
    during play). Fires at the first break of a new player's first game, before
    the mini-game shows; the headshot portrait fills the badge when the art has
    landed (emoji/pose fallback until). `onDone` continues into the break. */
export function triggerTheoIntro(line: string, onDone?: () => void): void {
  const ov = el('rankUpOverlay');
  if (!ov) {
    if (onDone) onDone(); // no DOM — never block the game flow
    return;
  }
  const badge = el('ruBadge');
  if (badge) {
    badge.className = `ru-badge char-art tier-${tierForRank(knownRankLevel)}`;
    // Best available introduction: the square intro video → headshot → pose art/emoji.
    badge.innerHTML = overlayVideoOk(introVideoAvail)
      ? overlayVideoInner(INTRO_VIDEO)
      : headshotAvail
        ? `<img class="char-art-media" src="${HEADSHOT_PATH}" alt="Brother Theo" />`
        : artInner('greet', false);
  }
  const title = el('ruTitle');
  if (title) title.textContent = 'Meet Brother Theo';
  const rk = el('ruRank');
  if (rk) rk.innerHTML = `${icon('candle', 14)} the Little Monk`;
  const sub = el('ruSub');
  if (sub) sub.textContent = line;
  presentRankOverlay(ov, onDone, 8000); // short auto-dismiss; tap continues sooner
}

/** Fill the between-rounds "Round complete" Theo badge (#nrTheo) with the open-arms
    welcome pose — real art when it has landed, emoji fallback until. Celebratory
    accent, not a dominant graphic. */
export function renderNextRoundTheo(): void {
  const badge = el('nrTheo');
  if (!badge) return;
  badge.innerHTML = artInner('welcome', false);
}

/* ---------- home greeting card ---------- */
let lastGreetMs: number | null = null;
let lastGreetDate: string | null = null;
let guestNudged = false; // once-per-session guest nudge (plan design ruling)

/** Drop the greeting throttle (and the cached rank brightness) so the NEXT
    renderCompanionHome re-greets from scratch. Call after every auth-state
    change — login, register, Apple, sign-out, delete — or the 5-minute throttle
    holds the previous identity's greeting on screen. */
export function forceCompanionRefresh(): void {
  lastGreetMs = null;
  lastGreetDate = null;
  knownRankLevel = 0; // authed players re-resolve from the profile on next render
}

/** Render / refresh Theo's greeting card (#theoCard) on the home screen. The
    5-min throttle + local-date rollover keeps re-entry from Arcade/history cheap.
    Chips (candle lit-state, streak) are refreshed on every call. */
export function renderCompanionHome(): void {
  const card = el('theoCard');
  if (!card) return;

  // Stage 0 (staged onboarding): Theo hasn't introduced himself yet — no
  // greeting card, no candle. He earns his entrance at the first game's break;
  // the throttle stays untouched so the first post-intro render greets fresh.
  if (!isTheoIntroduced()) {
    card.classList.add('hidden');
    card.innerHTML = '';
    return;
  }
  card.classList.remove('hidden');

  const now = Date.now();
  const today = localDateString();
  if (!greetingShouldRefresh(lastGreetMs, lastGreetDate, now, today)) {
    renderChips(card);
    return;
  }
  lastGreetMs = now;
  lastGreetDate = today;

  const period = greetingPeriod(new Date().getHours());
  const moment = `greeting-${period}` as Moment;
  const greeting = getLine('theo', moment);
  track('TheoShown', { moment, character: 'theo' });

  const tier = tierForRank(knownRankLevel);
  card.innerHTML =
    `<div class="theo-badge char-art tier-${tier}">${artInner('greet', true)}</div>` +
    `<div class="theo-body">` +
    `<div class="theo-name">${esc('Brother Theo')} <span class="theo-epithet">the Little Monk</span></div>` +
    `<div class="theo-line" id="theoGreetLine">${esc(greeting)}</div>` +
    (SHOW_FEAST_ON_HOME ? `<div class="theo-feast" id="theoFeastLine"></div>` : '') +
    `<div class="theo-chips" id="theoChips"></div>` +
    `</div>`;

  renderChips(card);

  // Feast sub-line (async; ET date passed explicitly by loadToday → api.getToday).
  // De-emphasized for now (Eric): not rendered as a home CTA. The moment stays
  // reachable via the quest-log card row + journey CTA at Tier 1.
  if (SHOW_FEAST_ON_HOME) {
    void loadToday().then((t) => {
      const line = el('theoFeastLine');
      if (line) line.textContent = t.feastLine;
    });
  }

  // Guests: tier-1 brightness + a single warm nudge line per session.
  if (!isAuthenticated() && !guestNudged) {
    guestNudged = true;
    track('TheoShown', { moment: 'guestNudge', character: 'theo' });
    triggerCharPop(getLine('theo', 'guestNudge'));
  }

  // Authed rank drives brightness + the streak chip.
  if (isAuthenticated()) {
    void getProfileMe().then((r) => {
      if (!r.ok || !r.data.stats) return;
      // Keep the disclosure gate fresh: an authed player with real server history
      // is never re-gated to the new-player home.
      setAuthedHistory(authedWithHistoryFromStats(r.data.stats));
      knownRankLevel = r.data.stats.rankLevel || rankForXp(r.data.stats.xp).level;
      const badge = card.querySelector<HTMLElement>('.theo-badge');
      if (badge) badge.className = `theo-badge char-art tier-${tierForRank(knownRankLevel)}`;
      const streak = el('streakChip');
      if (streak) streak.innerHTML = `${icon('flame', 14)} ${esc(String(r.data.stats.currentStreak))}-day streak`;
    });
  }
}

function renderChips(card: HTMLElement): void {
  const chips = card.querySelector<HTMLElement>('#theoChips');
  if (!chips) return;
  const lit = isLitToday();
  const name = currentRemembranceName();
  const candle = icon('candle', 14);
  const label = lit
    ? name
      ? `${candle} In memory of ${esc(name)}`
      : `${candle} Candle lit`
    : `${candle} Light a candle`;
  let html = `<button class="theo-chip candle${lit ? ' lit' : ''}" id="dailyChip" type="button">${label}</button>`;
  if (isAuthenticated()) {
    html += `<span class="theo-chip streak" id="streakChip">${icon('flame', 14)} streak</span>`;
  }
  chips.innerHTML = html;
}
